import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import type { UserSettings } from '@/types';

const CURRENCIES = [
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
  { code: 'MGA', symbol: 'Ar', label: 'Malagasy Ariary' },
] as const;

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
] as const;

type Step = 0 | 1 | 2 | 3;

const STEPS = [
  { icon: '👋', title: 'Welcome to MyBalance!', subtitle: 'Let\'s set you up in 3 quick steps.' },
  { icon: '💱', title: 'Choose your currency', subtitle: 'You can change this later in settings.' },
  { icon: '🌍', title: 'Choose your language', subtitle: 'MyBalance supports English and French.' },
  { icon: '🎯', title: 'Set a savings goal', subtitle: 'How much do you want to save this year? (Optional)' },
];

interface Props {
  onComplete: () => void;
}

export function OnboardingWizard({ onComplete }: Props) {
  const { updateSettings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(0);
  const [currency, setCurrency] = useState<UserSettings['currency']>('USD');
  const [language, setLanguage] = useState<UserSettings['language']>('en');
  const [yearlyGoal, setYearlyGoal] = useState('');
  const [saving, setSaving] = useState(false);

  const displayName = user?.email?.split('@')[0] || 'there';

  const handleFinish = async () => {
    setSaving(true);
    try {
      await updateSettings({
        currency,
        language,
        theme: 'system',
        yearlyGoal: parseFloat(yearlyGoal) || 0,
        defaultMonthlySalary: 0,
      });
      onComplete();
      navigate('/dashboard');
    } catch (e) {
      console.error('Onboarding save error:', e);
      // Don't block user even if save fails
      onComplete();
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  };

  const progress = (step / 3) * 100;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 px-4 py-12">
      {/* Background blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-purple-500/30 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-blue-500/30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
                  step >= n
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-slate-200 text-slate-400 dark:bg-slate-700'
                }`}
              >
                {step > n ? '✓' : n}
              </div>
            ))}
          </div>
          <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/90">
          {/* Step header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-xl">
              <span className="text-4xl">{STEPS[step].icon}</span>
            </div>
            <h1 className="mb-1 text-2xl font-black text-slate-900 dark:text-white">
              {step === 0 ? `Hey, ${displayName}! 👋` : STEPS[step].title}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">{STEPS[step].subtitle}</p>
          </div>

          {/* Step content */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="rounded-xl bg-blue-50 p-4 dark:bg-blue-900/20">
                <p className="text-sm leading-relaxed text-blue-800 dark:text-blue-300">
                  MyBalance helps you track your income and expenses, set savings goals, and understand where your money goes - all in a private, secure space.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { icon: '📊', label: 'Visual Charts' },
                  { icon: '🔄', label: 'Auto Recurring' },
                  { icon: '📥', label: 'Export Data' },
                ].map((f) => (
                  <div key={f.label} className="rounded-xl bg-slate-50 p-3 dark:bg-slate-700/50">
                    <p className="text-2xl mb-1">{f.icon}</p>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">{f.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCurrency(c.code as UserSettings['currency'])}
                  className={`flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all ${
                    currency === c.code
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-600 dark:hover:border-slate-500'
                  }`}
                >
                  <span className="text-xl font-bold text-slate-600 dark:text-slate-300 w-6 text-center">{c.symbol}</span>
                  <div>
                    <p className="text-xs font-bold text-slate-900 dark:text-white">{c.code}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{c.label}</p>
                  </div>
                  {currency === c.code && (
                    <span className="ml-auto text-blue-600 text-sm">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-3">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLanguage(l.code as UserSettings['language'])}
                  className={`flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                    language === l.code
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-600 dark:hover:border-slate-500'
                  }`}
                >
                  <span className="text-3xl">{l.flag}</span>
                  <span className="text-base font-bold text-slate-900 dark:text-white">{l.label}</span>
                  {language === l.code && (
                    <span className="ml-auto text-blue-600">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-slate-400">
                  {CURRENCIES.find((c) => c.code === currency)?.symbol}
                </span>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 5000"
                  value={yearlyGoal}
                  onChange={(e) => setYearlyGoal(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-base font-semibold text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                Skip this for now - you can set it later in Settings.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => (s - 1) as Step)}
                className="flex-none min-h-[48px] rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300"
              >
                ← Back
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep((s) => (s + 1) as Step)}
                className="flex-1 min-h-[48px] rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 py-2.5 text-sm font-bold text-white shadow-lg hover:opacity-90 transition-all active:scale-95"
              >
                {step === 0 ? "Let's go! →" : 'Continue →'}
              </button>
            ) : (
              <button
                onClick={handleFinish}
                disabled={saving}
                className="flex-1 min-h-[48px] rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 py-2.5 text-sm font-bold text-white shadow-lg hover:opacity-90 transition-all active:scale-95 disabled:opacity-70"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  '🎉 Start using MyBalance!'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Branding */}
        <p className="mt-6 text-center text-xs text-slate-400">
          MyBalance by{' '}
          <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Unova
          </span>
        </p>
      </div>
    </div>
  );
}
